import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import "../styles/ProjectsScreen.scss";
import {
  getAuthHeaders,
  getEffectiveAccessLevel,
  getProfile,
  isAdmin,
  isModerator,
  normalizeAccessLevel,
} from "../utils/auth";
import { apiFetch } from "../utils/api";
import ProjectStackEditor, { emptyProjectStack } from "../components/ProjectStackEditor";
import ProjectBannerImage from "../components/ProjectBannerImage";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const TASKS_LINK_TEXT = "перейти в проект";
const PROJECT_STATUSES = ["В работе", "Выполнен", "На паузе", "Проект запущен"];
const PROJECT_TYPES = [
  "Внутренний",
  "Внешний",
  "Исследовательский",
  "Учебный",
  "Сервисный",
];
const BASE_ROLES = ["Участник", "Разработчик", "Руководитель"];
const PROJECT_SORT_OPTIONS = [
  { value: "default", label: "По номеру и алфавиту" },
  { value: "title_asc", label: "Названию А→Я" },
  { value: "title_desc", label: "Названию Я→А" },
];

function normalizeUsername(value) {
  return value.replace(/^@/, "").trim().toLowerCase();
}

function leadingNumber(title) {
  const match = /^(\d+)/.exec((title || "").trim());
  return match ? Number(match[1]) : null;
}

function compareTitles(a, b) {
  return (a.title || "").localeCompare(b.title || "", "ru", {
    numeric: true,
    sensitivity: "base",
  });
}

function compareProjectsDefault(a, b) {
  const numA = leadingNumber(a.title);
  const numB = leadingNumber(b.title);
  if (numA !== null && numB !== null && numA !== numB) {
    return numA - numB;
  }
  if (numA !== null && numB === null) {
    return -1;
  }
  if (numA === null && numB !== null) {
    return 1;
  }
  return compareTitles(a, b);
}

function buildRoleString(baseRole, helper) {
  const safeBase = (baseRole || "Участник").trim() || "Участник";
  const roles = [safeBase];
  if (helper) {
    roles.push("Хелпер");
  }
  return roles.join(", ");
}

export default function ProjectsScreen() {
  const profile = useMemo(() => getProfile(), []);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(
    getEffectiveAccessLevel(profile)
  );
  const isGlobalAdmin = isAdmin(effectiveAccessLevel);
  const isGlobalModerator = isModerator(effectiveAccessLevel);
  const canCreateProject = isGlobalAdmin || isGlobalModerator;

  const [projects, setProjects] = useState([]);
  const [projectsError, setProjectsError] = useState("");
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimerRef = useState({ current: null })[0];
  const [createForm, setCreateForm] = useState({
    title: "",
    description: "",
    status: PROJECT_STATUSES[0],
    project_type: "",
	stack: emptyProjectStack(),
  });
  const [memberDraft, setMemberDraft] = useState({
    username: "",
    baseRole: "Участник",
    helper: false,
  });
  const [createMembers, setCreateMembers] = useState([]);
  const [createError, setCreateError] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [sortMode, setSortMode] = useState(() => {
    try {
      const saved = localStorage.getItem("projects_sort");
      return PROJECT_SORT_OPTIONS.some((option) => option.value === saved)
        ? saved
        : "default";
    } catch {
      return "default";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("projects_sort", sortMode);
    } catch {
      /* localStorage unavailable */
    }
  }, [sortMode]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingProjects(true);
    setProjectsError("");

    apiFetch(`${API_BASE}/projects`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setProjects(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setProjectsError("Не удалось загрузить проекты");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingProjects(false);
      });

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    setIsLoadingUsers(true);
    setUsersError("");

    apiFetch(`${API_BASE}/get_users`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        const list = Array.isArray(data) ? data : [];
        setUsers(list);
        const profileId = profile?.telegram_id;
        const profileUsername = profile?.username
          ? normalizeUsername(profile.username)
          : "";
        const matchedUser = list.find(
          (user) =>
            (profileId && user.telegram_id === profileId) ||
            (profileUsername &&
              normalizeUsername(user.username || "") === profileUsername)
        );
        if (matchedUser) {
          setEffectiveAccessLevel(
            normalizeAccessLevel(
              matchedUser.level_of_access || "",
              matchedUser.role || ""
            )
          );
        }
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setUsersError("Не удалось загрузить участников");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingUsers(false);
      });

    return () => {
      isActive = false;
    };
  }, [profile]);

  const normalizedProjects = useMemo(
    () => (Array.isArray(projects) ? projects : []),
    [projects]
  );

  const sortedProjects = useMemo(() => {
    const list = [...normalizedProjects];
    if (sortMode === "title_asc") {
      list.sort(compareTitles);
    } else if (sortMode === "title_desc") {
      list.sort((a, b) => compareTitles(b, a));
    } else {
      list.sort(compareProjectsDefault);
    }
    return list;
  }, [normalizedProjects, sortMode]);

  const userOptions = useMemo(() => {
    return users
      .filter((user) => user.username)
      .map((user) => ({
        username: user.username,
        fullName:
          user.full_name ||
          [user.first_name, user.last_name].filter(Boolean).join(" "),
        telegramId: user.telegram_id,
      }));
  }, [users]);

  const resetCreateForm = useCallback(() => {
    setCreateForm({
      title: "",
      description: "",
      status: PROJECT_STATUSES[0],
      project_type: "",
	  stack: emptyProjectStack(),
    });
    setMemberDraft({
      username: "",
      baseRole: "Участник",
      helper: false,
    });
    setCreateMembers([]);
    setCreateError("");
  }, []);

  const openCreateModal = () => {
    if (!canCreateProject) {
      return;
    }
    setIsClosing(false);
    resetCreateForm();
    setIsCreateOpen(true);
  };

  const closeCreateModal = useCallback(() => {
    if (isClosing) {
      return;
    }
    setIsClosing(true);
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setIsCreateOpen(false);
      setIsClosing(false);
      closeTimerRef.current = null;
    }, 260);
  }, [closeTimerRef, isClosing]);

  useEffect(() => {
    if (!isCreateOpen) {
      return;
    }
    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        closeCreateModal();
      }
    };
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [closeCreateModal, isCreateOpen]);

  const handleCreateFormChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleMemberDraftChange = (event) => {
    const { name, value, type, checked } = event.target;
    setMemberDraft((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMemberEditChange = (username, field, value) => {
    setCreateMembers((prev) =>
      prev.map((member) =>
        member.username === username
          ? { ...member, [field]: value }
          : member
      )
    );
  };

  const handleAddMember = (event) => {
    event.preventDefault();
    if (!canCreateProject) {
      return;
    }
    setCreateError("");
    const normalized = normalizeUsername(memberDraft.username);
    if (!normalized) {
      setCreateError("Введите username участника");
      return;
    }
    if (createMembers.some((member) => member.username === normalized)) {
      setCreateError("Участник уже добавлен");
      return;
    }
    const userInfo = userOptions.find(
      (user) => normalizeUsername(user.username || "") === normalized
    );
    setCreateMembers((prev) => [
      ...prev,
      {
        username: normalized,
        full_name: userInfo?.fullName || "",
        telegram_id: userInfo?.telegramId || "",
        baseRole: memberDraft.baseRole,
        helper: memberDraft.helper,
      },
    ]);
    setMemberDraft({
      username: "",
      baseRole: "Участник",
      helper: false,
    });
  };

  const handleRemoveMember = (username) => {
    setCreateMembers((prev) =>
      prev.filter((member) => member.username !== username)
    );
  };

  const handleCreateProject = () => {
    if (!canCreateProject || isCreating) {
      return;
    }
    const title = createForm.title.trim();
    if (!title) {
      setCreateError("Укажите название проекта");
      return;
    }
    setCreateError("");
    setIsCreating(true);

    const membersPayload = createMembers.map((member) => ({
      username: member.username,
      full_name: member.full_name || "",
      role: buildRoleString(member.baseRole, member.helper),
      telegram_id: member.telegram_id || "",
    }));

    apiFetch(`${API_BASE}/projects/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        title,
        description: createForm.description.trim(),
        status: createForm.status.trim(),
        project_type: createForm.project_type.trim(),
		stack: createForm.stack,
        members: membersPayload,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        setProjects((prev) => {
          const next = Array.isArray(prev) ? [...prev] : [];
          next.push(data);
          return next;
        });
        closeCreateModal();
      })
      .catch(() => setCreateError("Не удалось создать проект"))
      .finally(() => setIsCreating(false));
  };

  const overlayContent = isCreateOpen ? (
    <div
      className={`projects-screen__overlay${isClosing ? " is-closing" : ""}`}
      onClick={closeCreateModal}
    >
      <div
        className={`projects-modal${isClosing ? " is-closing" : ""}`}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="projects-modal__header">
          <div>
            <p className="projects-modal__eyebrow">Новый проект</p>
            <h3 className="projects-modal__title">Создание проекта</h3>
          </div>
          <button
            type="button"
            className="projects-modal__close"
            onClick={closeCreateModal}
            aria-label="Закрыть"
          >
            <span />
            <span />
          </button>
        </div>
        {createError && <p className="projects-modal__error">{createError}</p>}
        <div className="projects-modal__form">
          <label>
            Название
            <input
              name="title"
              value={createForm.title}
              onChange={handleCreateFormChange}
              placeholder="Название проекта"
            />
          </label>
          <label className="projects-modal__form--full">
            Описание
            <textarea
              name="description"
              value={createForm.description}
              onChange={handleCreateFormChange}
              rows={3}
              placeholder="Кратко о проекте"
            />
          </label>
          <label>
            Статус
            <select
              name="status"
              value={createForm.status}
              onChange={handleCreateFormChange}
            >
              {PROJECT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Тип проекта
            <input
              list="project-types-list"
              name="project_type"
              value={createForm.project_type}
              onChange={handleCreateFormChange}
              placeholder="Например: Внутренний"
            />
          </label>
        </div>

		<div className="projects-modal__section">
		  <div className="projects-modal__section-header">
			<h4>Стек проекта</h4>
		  </div>
		  <p className="projects-modal__hint">Выберите технологии по контурам. Можно добавить свой вариант.</p>
		  <ProjectStackEditor
			value={createForm.stack}
			onChange={(stack) => setCreateForm((prev) => ({ ...prev, stack }))}
		  />
		</div>

        <div className="projects-modal__section">
          <div className="projects-modal__section-header">
            <h4>Участники</h4>
            <span className="projects-modal__count">
              {createMembers.length}
            </span>
          </div>
          <form
            className="projects-modal__member-form"
            onSubmit={handleAddMember}
          >
            <label>
              Username
              <input
                list="project-users-list"
                name="username"
                value={memberDraft.username}
                onChange={handleMemberDraftChange}
                placeholder="@username"
              />
            </label>
            <label>
              Роль
              <select
                name="baseRole"
                value={memberDraft.baseRole}
                onChange={handleMemberDraftChange}
              >
                {BASE_ROLES.map((roleOption) => (
                  <option key={roleOption} value={roleOption}>
                    {roleOption}
                  </option>
                ))}
              </select>
            </label>
            <label className="projects-modal__toggle checkbox">
              <input
                type="checkbox"
                name="helper"
                checked={memberDraft.helper}
                onChange={handleMemberDraftChange}
              />
              <span>Хелпер</span>
            </label>
            <button type="submit" className="projects-modal__add">
              Добавить
            </button>
          </form>
          {usersError && (
            <p className="projects-modal__hint">{usersError}</p>
          )}
          {isLoadingUsers && (
            <p className="projects-modal__hint">Загрузка списка участников...</p>
          )}
          <div className="projects-modal__member-list">
            {createMembers.map((member) => (
              <div className="projects-modal__member-row" key={member.username}>
                <div className="projects-modal__member-info">
                  <p>{member.full_name || member.username}</p>
                  <span>@{member.username}</span>
                </div>
                <div className="projects-modal__member-controls">
                  <select
                    value={member.baseRole}
                    onChange={(event) =>
                      handleMemberEditChange(
                        member.username,
                        "baseRole",
                        event.target.value
                      )
                    }
                  >
                    {BASE_ROLES.map((roleOption) => (
                      <option key={roleOption} value={roleOption}>
                        {roleOption}
                      </option>
                    ))}
                  </select>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={member.helper}
                      onChange={(event) =>
                        handleMemberEditChange(
                          member.username,
                          "helper",
                          event.target.checked
                        )
                      }
                    />
                    <span>Хелпер</span>
                  </label>
                </div>
                <button
                  type="button"
                  className="projects-modal__remove"
                  onClick={() => handleRemoveMember(member.username)}
                >
                  Удалить
                </button>
              </div>
            ))}
            {createMembers.length === 0 && (
              <p className="projects-modal__hint">
                Добавьте участников проекта (опционально).
              </p>
            )}
          </div>
        </div>
        <datalist id="project-users-list">
          {userOptions.map((user) => (
            <option key={user.username} value={user.username}>
              {user.fullName || user.username}
            </option>
          ))}
        </datalist>
        <datalist id="project-types-list">
          {PROJECT_TYPES.map((typeOption) => (
            <option key={typeOption} value={typeOption} />
          ))}
        </datalist>
        <div className="projects-modal__actions">
          <button
            type="button"
            className="projects-modal__primary"
            onClick={handleCreateProject}
            disabled={!canCreateProject || isCreating}
          >
            {isCreating ? "Создание..." : "Создать проект"}
          </button>
          <button
            type="button"
            className="projects-modal__secondary"
            onClick={closeCreateModal}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <section className="projects-screen">
      <header className="projects-screen__header">
        <div>
          <p className="projects-screen__eyebrow">Каталог</p>
          <h2 className="projects-screen__title">Все проекты</h2>
        </div>
        <div className="projects-screen__actions">
          <label className="projects-screen__sort">
            <span>Сортировка</span>
            <select
              name="projectSort"
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              aria-label="Сортировать проекты"
            >
              {PROJECT_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <span className="projects-screen__count">
            {normalizedProjects.length}
          </span>
          {canCreateProject && (
            <button
              type="button"
              className="projects-screen__create"
              onClick={openCreateModal}
              aria-label="Создать проект"
            >
              +
            </button>
          )}
        </div>
      </header>
      <div className="projects-screen__list">
        {isLoadingProjects && (
          <article className="project-card" aria-busy="true">
            <div className="project-card__main">
              <p className="project-card__title">Загрузка...</p>
              <p className="project-card__subtitle">подождите немного</p>
            </div>
          </article>
        )}
        {!isLoadingProjects && projectsError && (
          <article className="project-card">
            <div className="project-card__main">
              <p className="project-card__title">Ошибка</p>
              <p className="project-card__subtitle">{projectsError}</p>
            </div>
          </article>
        )}
        {!isLoadingProjects &&
          !projectsError &&
          normalizedProjects.length === 0 && (
            <article className="project-card">
              <div className="project-card__main">
                <p className="project-card__title">Проекты не найдены</p>
                <p className="project-card__subtitle">
                  пока нет загруженных карточек
                </p>
              </div>
            </article>
          )}
        {!isLoadingProjects &&
          !projectsError &&
          sortedProjects.length > 0 &&
          sortedProjects.map((project) => {
            const members = Array.isArray(project.members)
              ? project.members
              : [];
            return (
              <Link
                className="project-link"
                to={`/projects/${project.id}`}
                key={project.id}
              >
                <article className="project-card">
                  <ProjectBannerImage
                    bannerUrl={project.banner_url}
                    title={project.title}
                    className="project-card__banner"
                  />
                  <div className="project-card__main">
                    <p className="project-card__title">{project.title}</p>
                    <p className="project-card__subtitle">{TASKS_LINK_TEXT}</p>
                  </div>
                  <div className="project-card__tags">
                    <span className="project-card__tag">
                      статус: {project.status || "—"}
                    </span>
                    <span className="project-card__tag">
                      тип: {project.project_type || "не указан"}
                    </span>
                    <span className="project-card__tag is-muted">
                      участники: {members.length}
                    </span>
                  </div>
                </article>
              </Link>
            );
          })}
      </div>
      {overlayContent && createPortal(overlayContent, document.body)}
    </section>
  );
}
